import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { SearchIcon } from '@heroicons/react/outline';
import {
  giftCouponSearchDataAtomFamily,
  giftCouponSearchStatusAtomFamily,
  giftCouponStatisticsDataAtom,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  useGiftCouponSearchHook,
  useGiftCouponStatisticsHook,
} from '@shared/api/giftCoupon/giftCoupon.hook';
import {
  GiftCouponForRedemption,
  GiftCouponRedeemStatus,
  GiftCouponSearchParams,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Loading } from '@shared/components/Loading';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { Link } from 'react-router-dom';

const getStatusBadge = (status: GiftCouponRedeemStatus) => {
  switch (status) {
    case GiftCouponRedeemStatus.Requested:
      return { text: '신청됨', className: 'bg-yellow-100 text-yellow-800' };
    case GiftCouponRedeemStatus.Delivered:
      return { text: '발송완료', className: 'bg-green-100 text-green-800' };
    case GiftCouponRedeemStatus.Rejected:
      return { text: '발송불가', className: 'bg-orange-100 text-orange-800' };
    case GiftCouponRedeemStatus.Expired:
      return { text: '만료', className: 'bg-gray-100 text-gray-800' };
    case GiftCouponRedeemStatus.Refunded:
      return { text: '환불', className: 'bg-red-100 text-red-800' };
    case GiftCouponRedeemStatus.Canceled:
      return { text: '취소', className: 'bg-gray-100 text-gray-500' };
    default:
      return { text: '대기', className: 'bg-blue-100 text-blue-800' };
  }
};

const formatDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  return `${y}.${m}.${day} ${h}시`;
};

const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

export const GiftCouponHistorySearchPage: FC = function GiftCouponHistorySearchPage() {
  useGiftCouponStatisticsHook();
  const statistics = useRecoilValue(giftCouponStatisticsDataAtom);

  const [showBlacklist, setShowBlacklist] = useState(false);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMessage(msg);
    toastTimer.current = setTimeout(() => setToastMessage(null), 1200);
  }, []);
  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  // 검색 입력 상태
  const [phoneNumber, setPhoneNumber] = useState('');
  const [ordererName, setOrdererName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [redeemStatus, setRedeemStatus] = useState<string>('');

  // 정렬 상태
  type SortField = 'requestedAt' | 'purchasedAt' | 'deliveredAt';
  type SortDirection = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('requestedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((d) => (d === 'desc' ? 'asc' : 'desc'));
        return prev;
      }
      setSortDirection('desc');
      return field;
    });
  }, []);

  // 검색 실행 상태
  const [searchKey, setSearchKey] = useState('');
  const [searchParams, setSearchParams] = useState<GiftCouponSearchParams | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  useGiftCouponSearchHook(searchKey, searchParams || {}, !searchKey || !searchParams);

  const status = useRecoilValue(giftCouponSearchStatusAtomFamily(searchKey));
  const searchResults = useRecoilValue(giftCouponSearchDataAtomFamily(searchKey));

  const handleSearch = useCallback(() => {
    if (!phoneNumber && !ordererName && !dateFrom && !dateTo && !redeemStatus) {
      alert('검색 조건을 하나 이상 입력해주세요.');
      return;
    }

    const params: GiftCouponSearchParams = {};
    if (phoneNumber) params.phoneNumber = phoneNumber.replace(/[^0-9]/g, '');
    if (ordererName) params.ordererName = ordererName;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (redeemStatus) params.redeemStatus = redeemStatus as GiftCouponRedeemStatus;

    const key = JSON.stringify(params);
    setSearchKey(key);
    setSearchParams(params);
    setHasSearched(true);
  }, [phoneNumber, ordererName, dateFrom, dateTo, redeemStatus]);

  const searchByStatus = useCallback((status: GiftCouponRedeemStatus) => {
    setPhoneNumber('');
    setOrdererName('');
    setDateFrom('');
    setDateTo('');
    setRedeemStatus(status);
    setTimeout(() => {
      const params: GiftCouponSearchParams = { redeemStatus: status };
      const key = JSON.stringify(params);
      setSearchKey(key);
      setSearchParams(params);
      setHasSearched(true);
    }, 0);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  const getOrdererInfoForSort = useCallback((coupon: GiftCouponForRedemption) => {
    const order = typeof coupon.giftOrder === 'object' ? coupon.giftOrder : null;
    return order?.paymentDetail?.purchasedAt;
  }, []);

  const results = useMemo(() => {
    if (!searchResults) return [];
    return [...searchResults].sort((a, b) => {
      let aDate: Date | string | undefined;
      let bDate: Date | string | undefined;

      switch (sortField) {
        case 'purchasedAt':
          aDate = getOrdererInfoForSort(a);
          bDate = getOrdererInfoForSort(b);
          break;
        case 'deliveredAt':
          aDate = a.gifticon?.deliveredAt;
          bDate = b.gifticon?.deliveredAt;
          break;
        case 'requestedAt':
        default:
          aDate = a.recipient?.requestedAt;
          bDate = b.recipient?.requestedAt;
          break;
      }

      const aTime = aDate ? new Date(aDate).getTime() : 0;
      const bTime = bDate ? new Date(bDate).getTime() : 0;
      return sortDirection === 'desc' ? bTime - aTime : aTime - bTime;
    });
  }, [searchResults, sortField, sortDirection, getOrdererInfoForSort]);

  const formatPhone = useCallback((phone: string | undefined) => {
    if (!phone) return '-';
    const numbers = phone.replace(/[^0-9]/g, '');
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    return phone;
  }, []);

  const getOrdererInfo = useCallback((coupon: GiftCouponForRedemption) => {
    const order = typeof coupon.giftOrder === 'object' ? coupon.giftOrder : null;
    if (!order) return { name: '-', phone: '-', purchasedAt: undefined };
    return {
      name: order.bugo?.member?.fullName || '-',
      phone: order.bugo?.member?.phoneNumber || '-',
      purchasedAt: order.paymentDetail?.purchasedAt,
    };
  }, []);

  const getItemName = useCallback((coupon: GiftCouponForRedemption) => {
    if (typeof coupon.giftItem === 'object') {
      return coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || '-';
    }
    return '-';
  }, []);

  const getChoiceUnitName = useCallback((coupon: GiftCouponForRedemption) => {
    if (coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object') {
      return coupon.selectedChoiceUnit.name;
    }
    return null;
  }, []);

  const getImageUrl = useCallback((coupon: GiftCouponForRedemption) => {
    if (
      coupon.selectedChoiceUnit &&
      typeof coupon.selectedChoiceUnit === 'object' &&
      coupon.selectedChoiceUnit.image
    ) {
      const img = coupon.selectedChoiceUnit.image;
      const imgUrl = typeof img === 'object' && 'url' in img ? img.url : img;
      if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
    }
    if (typeof coupon.giftItem === 'object') {
      const giftItemImg = coupon.giftItem?.image;
      if (giftItemImg) {
        const imgUrl =
          typeof giftItemImg === 'object' && 'url' in giftItemImg
            ? giftItemImg.url
            : giftItemImg;
        if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
      }
      const giftItemInfoImg = coupon.giftItem?.giftItemInfo?.image;
      if (giftItemInfoImg) {
        const imgUrl =
          typeof giftItemInfoImg === 'object' && 'url' in giftItemInfoImg
            ? giftItemInfoImg.url
            : giftItemInfoImg;
        if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
      }
    }
    return '';
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">쿠폰 내역 검색</h1>
            <p className="mt-1 text-sm text-gray-500">
              기간, 전화번호, 주문자명 등으로 쿠폰 내역을 검색하세요.
            </p>
          </div>
          <Link
            to="/gift-seller/coupon-redemption"
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            발송 대기 목록
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-6 gap-3 p-4">
        <div className="rounded-lg bg-yellow-50 p-3 text-center">
          <p className="text-lg font-bold text-yellow-700">
            {statistics?.pending ?? '-'}
          </p>
          <p className="text-xs text-yellow-600">발송 대기</p>
        </div>
        <div className="rounded-lg bg-green-50 p-3 text-center">
          <p className="text-lg font-bold text-green-700">
            {statistics?.delivered ?? '-'}
          </p>
          <p className="text-xs text-green-600">발송 완료</p>
        </div>
        <div className="rounded-lg bg-gray-100 p-3 text-center">
          <p className="text-lg font-bold text-gray-700">{statistics?.total ?? '-'}</p>
          <p className="text-xs text-gray-600">전체</p>
        </div>
        <button
          onClick={() => setShowBlacklist((prev) => !prev)}
          className={`rounded-lg p-3 text-center transition-colors ${
            statistics?.blacklist && statistics.blacklist.length > 0
              ? 'bg-red-50 hover:bg-red-100'
              : 'bg-gray-50'
          } ${showBlacklist ? 'ring-2 ring-red-400' : ''}`}
        >
          <p
            className={`text-lg font-bold ${
              statistics?.blacklist && statistics.blacklist.length > 0
                ? 'text-red-700'
                : 'text-gray-400'
            }`}
          >
            {statistics?.blacklist?.length ?? 0}
          </p>
          <p
            className={`text-xs ${
              statistics?.blacklist && statistics.blacklist.length > 0
                ? 'text-red-600'
                : 'text-gray-400'
            }`}
          >
            중복 의심
          </p>
        </button>
        <button
          onClick={() => searchByStatus(GiftCouponRedeemStatus.Rejected)}
          className={`rounded-lg p-3 text-center transition-colors ${
            statistics?.rejected ? 'bg-orange-50 hover:bg-orange-100' : 'bg-gray-50'
          } ${
            redeemStatus === GiftCouponRedeemStatus.Rejected && hasSearched
              ? 'ring-2 ring-orange-400'
              : ''
          }`}
        >
          <p
            className={`text-lg font-bold ${
              statistics?.rejected ? 'text-orange-700' : 'text-gray-400'
            }`}
          >
            {statistics?.rejected ?? 0}
          </p>
          <p className="text-xs text-orange-600">발송불가</p>
        </button>
        <button
          onClick={() => searchByStatus(GiftCouponRedeemStatus.Canceled)}
          className={`rounded-lg p-3 text-center transition-colors ${
            statistics?.canceled ? 'bg-gray-100 hover:bg-gray-200' : 'bg-gray-50'
          } ${
            redeemStatus === GiftCouponRedeemStatus.Canceled && hasSearched
              ? 'ring-2 ring-gray-400'
              : ''
          }`}
        >
          <p
            className={`text-lg font-bold ${
              statistics?.canceled ? 'text-gray-700' : 'text-gray-400'
            }`}
          >
            {statistics?.canceled ?? 0}
          </p>
          <p className="text-xs text-gray-500">취소</p>
        </button>
      </div>

      {/* 블랙리스트 */}
      {showBlacklist && statistics?.blacklist && statistics.blacklist.length > 0 && (
        <div className="mx-4 mb-4 overflow-hidden rounded-lg bg-white shadow">
          <div className="border-b bg-red-50 px-4 py-3">
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-red-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm font-medium text-red-800">
                중복 의심 번호 ({statistics.blacklist.length}건)
              </p>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {statistics.blacklist.map((item) => (
              <div
                key={item.phoneNumber}
                className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50"
              >
                <button
                  onClick={() => copyToClipboard(item.phoneNumber)}
                  className="shrink-0 font-mono text-sm font-medium text-gray-900 hover:text-indigo-600"
                  title="클릭하여 복사"
                >
                  {formatPhone(item.phoneNumber)}
                </button>
                <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">
                  {item.count}건
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap gap-1">
                    {item.names.map((name, idx) => (
                      <span
                        key={idx}
                        className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                  {item.names.length >= 2 && (
                    <p className="mt-0.5 text-[10px] text-red-500">다른 이름으로 신청</p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setPhoneNumber(item.phoneNumber);
                    setRedeemStatus('');
                    setOrdererName('');
                    setDateFrom('');
                    setDateTo('');
                    setTimeout(() => {
                      const params: GiftCouponSearchParams = {
                        phoneNumber: item.phoneNumber.replace(/[^0-9]/g, ''),
                      };
                      const key = JSON.stringify(params);
                      setSearchKey(key);
                      setSearchParams(params);
                      setHasSearched(true);
                    }, 0);
                  }}
                  className="shrink-0 rounded bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-200"
                >
                  검색
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 검색 폼 */}
      <div className="mx-4 rounded-lg bg-white p-4 shadow">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              수신자 전화번호
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="010-1234-5678"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">주문자명</label>
            <input
              type="text"
              value={ordererName}
              onChange={(e) => setOrdererName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="홍길동"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">상태</label>
            <select
              value={redeemStatus}
              onChange={(e) => setRedeemStatus(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">전체 상태</option>
              <option value={GiftCouponRedeemStatus.Available}>교환가능</option>
              <option value={GiftCouponRedeemStatus.Requested}>신청됨</option>
              <option value={GiftCouponRedeemStatus.Delivered}>발송완료</option>
              <option value={GiftCouponRedeemStatus.Rejected}>발송불가</option>
              <option value={GiftCouponRedeemStatus.Expired}>만료</option>
              <option value={GiftCouponRedeemStatus.Refunded}>환불</option>
              <option value={GiftCouponRedeemStatus.Canceled}>취소</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">시작일</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">종료일</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              className="flex items-center gap-2 rounded-md bg-indigo-600 px-6 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <SearchIcon className="h-4 w-4" />
              검색
            </button>
          </div>
        </div>
      </div>

      {/* 검색 결과 */}
      <div className="p-4">
        {hasSearched ? (
          <div className="overflow-hidden rounded-lg bg-white shadow">
            {status === 'loading' ? (
              <div className="center-box py-12">
                <Loading />
              </div>
            ) : (
              <>
                <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-sm text-gray-500">검색 결과: {results.length}건</p>
                </div>
                {results.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            상태
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            쿠폰번호
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            _id
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            상품
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            주문자
                          </th>
                          <th
                            className="cursor-pointer select-none px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-indigo-600"
                            onClick={() => handleSort('purchasedAt')}
                          >
                            <span className="inline-flex items-center gap-1">
                              구매일
                              {sortField === 'purchasedAt' ? (
                                <span className="text-indigo-600">
                                  {sortDirection === 'desc' ? '▼' : '▲'}
                                </span>
                              ) : (
                                <span className="text-gray-300">▼</span>
                              )}
                            </span>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            수신자
                          </th>
                          <th
                            className="cursor-pointer select-none px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-indigo-600"
                            onClick={() => handleSort('requestedAt')}
                          >
                            <span className="inline-flex items-center gap-1">
                              신청일시
                              {sortField === 'requestedAt' ? (
                                <span className="text-indigo-600">
                                  {sortDirection === 'desc' ? '▼' : '▲'}
                                </span>
                              ) : (
                                <span className="text-gray-300">▼</span>
                              )}
                            </span>
                          </th>
                          <th
                            className="cursor-pointer select-none px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-indigo-600"
                            onClick={() => handleSort('deliveredAt')}
                          >
                            <span className="inline-flex items-center gap-1">
                              발송일시
                              {sortField === 'deliveredAt' ? (
                                <span className="text-indigo-600">
                                  {sortDirection === 'desc' ? '▼' : '▲'}
                                </span>
                              ) : (
                                <span className="text-gray-300">▼</span>
                              )}
                            </span>
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                            도매가
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            만료일
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {results.map((coupon) => {
                          const badge = getStatusBadge(coupon.redeemStatus);
                          const choiceUnitName = getChoiceUnitName(coupon);
                          const ordererInfo = getOrdererInfo(coupon);

                          return (
                            <tr key={coupon._id} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-4 py-3">
                                <span
                                  className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${badge.className}`}
                                >
                                  {badge.text}
                                </span>
                              </td>

                              <td className="whitespace-nowrap px-4 py-3">
                                <span className="font-mono text-sm text-gray-700">
                                  {coupon.fullCode || '-'}
                                </span>
                              </td>

                              <td className="whitespace-nowrap px-4 py-3">
                                <span className="font-mono text-sm text-gray-700">
                                  {coupon._id || '-'}
                                </span>
                              </td>

                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  {getImageUrl(coupon) && (
                                    <img
                                      src={getImageUrl(coupon)}
                                      alt=""
                                      className="h-8 w-8 rounded object-cover"
                                    />
                                  )}
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {getItemName(coupon)}
                                    </p>
                                    {choiceUnitName && (
                                      <p className="max-w-[180px] truncate text-xs text-indigo-600">
                                        {choiceUnitName}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </td>

                              <td className="whitespace-nowrap px-4 py-3">
                                <p className="text-sm text-gray-900">
                                  {ordererInfo.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatPhone(ordererInfo.phone)}
                                </p>
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                                {formatDate(ordererInfo.purchasedAt)}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3">
                                <p className="text-sm text-gray-900">
                                  {formatPhone(coupon.recipient?.phoneNumber)}
                                </p>
                                {coupon.recipient?.name && (
                                  <p className="text-xs text-gray-500">
                                    {coupon.recipient.name}
                                  </p>
                                )}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                                {formatDateTime(coupon.recipient?.requestedAt)}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                                {coupon.redeemStatus === GiftCouponRedeemStatus.Delivered
                                  ? formatDateTime(coupon.gifticon?.deliveredAt)
                                  : '-'}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-indigo-600">
                                {coupon.redeemStatus ===
                                  GiftCouponRedeemStatus.Delivered &&
                                coupon.gifticon?.priceWhole != null
                                  ? `${coupon.gifticon.priceWhole.toLocaleString()}원`
                                  : '-'}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                                {formatDate(coupon.expiresAt)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <p className="text-gray-500">검색 결과가 없습니다.</p>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="rounded-lg bg-white p-12 text-center shadow">
            <SearchIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">쿠폰 내역 검색</h3>
            <p className="mt-1 text-sm text-gray-500">
              검색 조건을 입력하고 검색 버튼을 눌러주세요.
            </p>
          </div>
        )}
      </div>

      {/* 토스트 */}
      {toastMessage && (
        <div className="pointer-events-none fixed inset-x-0 bottom-8 z-[100] flex justify-center">
          <div className="animate-fade-in-out rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white shadow-lg">
            {toastMessage}
          </div>
        </div>
      )}

      <style>{`
          @keyframes fadeInOut {
            0% { opacity: 0; transform: translateY(8px); }
            15% { opacity: 1; transform: translateY(0); }
            75% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-4px); }
          }
          .animate-fade-in-out {
            animation: fadeInOut 1.2s ease-in-out forwards;
          }
        `}</style>
    </div>
  );
};
